import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APICommonService } from '@nationwide/internet-servicing-angular-services';
import { Observable, forkJoin } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { commercialConstants } from '../../../common/commercial-constants';
import { EnvironmentpickerService } from '../../../shared/services/environmentpicker.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { TokenDataService } from '../../../shared/services/token-data.service';
import { UtilService } from '../../../shared/services/util.service';
import { CommercialLoggingService } from './commercial-logging.service';

@Injectable()
export class SearchForClaimsService {
    constructor(private httpClient: HttpClient,
        private apiCommonService: APICommonService,
        private tokenDataService: TokenDataService,
        private util: UtilService,
        private LOGGER: LoggerService,
        private environmentPicker: EnvironmentpickerService,
        private commercialLoggingService: CommercialLoggingService
    ) { }
    buildQueryParams(showClosed: boolean, offset: number): string {
        const limit = commercialConstants.claimConstants.limit;
        let queryParameters = `?claimState=open&incidentOnly=true&limit=${limit}`;
        if (showClosed) {
            queryParameters = `?incidentOnly=true&limit=${limit}`;
        }
        queryParameters = `${queryParameters}&offset=${offset}`;
        return queryParameters;
    }

    retrieveOpenClosedClaims(showClosed: boolean, accountNumber: string): Promise<any> {
        return new Promise((resolve) => {
            const limit = commercialConstants.claimConstants.limit;
            let offset = 0;
            const requestBody = { accountNumber };
            const claimSummaryList = { claimSummaries: [] };
            this.searchClaims(requestBody, this.buildQueryParams(showClosed, offset)).subscribe((response) => {
                this.commercialLoggingService.logServiceSuccess(
                    this.LOGGER,
                    'Commercial Claims Service',
                    'SearchForClaimsService',
                    'retrieveOpenClosedClaims',
                    this.getServiceLogMetadata({ claimsDisplayCount: response?.headers?.get('X-Total-Count') || '0' })
                );
                const numberOfClaims = response.headers.get('X-Total-Count');
                if (response.body?.claimSummaries) {
                    response.body['claimSummaries'].forEach((value: any) => {
                        claimSummaryList.claimSummaries.push(value);
                    });
                }
                let splitLimit: number;
                if (numberOfClaims > limit) {
                    splitLimit = Math.ceil(numberOfClaims / limit) - 1;
                    const forkArray = [];
                    for (let count = 0; count < splitLimit; count++) {
                        offset = offset + limit;
                        forkArray.push(this.searchClaims(requestBody, this.buildQueryParams(showClosed, offset)));
                    }
                    forkJoin(forkArray).subscribe((forkResponse) => {
                        for (const value of forkResponse) {
                            if (value?.['body.claimSummaries']) {
                                for (const claim of value['body']['claimSummaries']) {
                                    claimSummaryList.claimSummaries.push(claim);
                                }
                            }
                        }
                        this.commercialLoggingService.logServiceSuccess(
                            this.LOGGER,
                            'Commercial Claims Service',
                            'SearchForClaimsService',
                            'retrieveOpenClosedClaimsBatch',
                            this.getServiceLogMetadata({ claimsDisplayCount: claimSummaryList.claimSummaries.length })
                        );
                        resolve(claimSummaryList);
                    }, (error) => {
                        this.commercialLoggingService.logServiceFailure(
                            this.LOGGER,
                            'Commercial Claims Service',
                            'SearchForClaimsService',
                            'retrieveOpenClosedClaimsBatch',
                            this.getServiceLogMetadata(),
                            error
                        );
                        resolve(claimSummaryList);
                    });
                } else {
                    resolve(claimSummaryList);
                }
            }, (error) => {
                this.commercialLoggingService.logServiceFailure(
                    this.LOGGER,
                    'Commercial Claims Service',
                    'SearchForClaimsService',
                    'retrieveOpenClosedClaims',
                    this.getServiceLogMetadata(),
                    error
                );
                resolve(claimSummaryList);
            });
        });
    }
    searchClaims(requestBody: any, queryParameters?: string): Observable<any> {
        let searchForClaimsUrl = `${this.environmentPicker.getEnvironmentClaimsCenterApiURL() + environment.CLAIMS_API.GET_BASIC_CLAIMS_DETAILS}claims/search`;
        if (queryParameters) {
            searchForClaimsUrl = searchForClaimsUrl + queryParameters;
        }
        const appClientId = environment.appProps.appClientId;
        const authToken = this.tokenDataService.getToken();
        const sessionId = window.sessionStorage.getItem('sessionId');
        let headers = this.apiCommonService.buildHeader({ appClientId, authToken, contentType: 'application/json', headerType: 'X-NW-Message-ID', sessionId });

        const serverId = this.environmentPicker.getEnvironmentServerNumber();
        if (serverId) {
            headers = headers.append('X-NW-Server-ID-Number', serverId);
        }
        headers = headers.append('X-NW-Message-ID', this.apiCommonService.generateTransactionId());
        headers = headers.delete('x-nw-sessionid');
        this.commercialLoggingService.logServiceSuccess(
            this.LOGGER,
            'Commercial Claims Service',
            'SearchForClaimsService',
            'searchClaimsRequestBuilt',
            this.getServiceLogMetadata()
        );

        return this.httpClient.post(searchForClaimsUrl, requestBody, { headers, observe: 'response' })
            .pipe(catchError((error: HttpErrorResponse) => this.util.handleError(`Error searching claims with URL: "${searchForClaimsUrl}"`, error)));
    }

    private getServiceLogMetadata(additionalFields: any = {}): any {
        return {
            policyNumber: window.sessionStorage.getItem('claimsSelectedPolicyNumber') || '',
            policyType: window.sessionStorage.getItem('claimsPolicyType') || 'UNKNOWN',
            lineOfBusiness: window.sessionStorage.getItem('fnolFlow') || 'UNKNOWN',
            claimsDisplayCount: additionalFields.claimsDisplayCount || 'UNKNOWN',
            alphaState: 'UNKNOWN',
            previousUrl: window.sessionStorage.getItem('previousUrl') || '',
            ...additionalFields
        };
    }
    // retrieveClaims(showClosed?: boolean, offset?: number, limit?: number): Observable<any> {
    //   const requestBody = { accountNumber: this.claimsDataService.getClaimsAccountNumber() };
    //   let queryParams = '';
    //   if (showClosed) {
    //     queryParams = '?incidentOnly=true&limit=' + limit;
    //   } else {
    //     queryParams = '?claimState=open&incidentOnly=true&limit=' + limit;
    //   }
    //   queryParams = queryParams + '&offset=' + offset;
    //   return this.searchClaims(requestBody, queryParams);
    // }

    stubSummaries(response: any, checkStub = false): any {
        if (checkStub) {
            const returnValue = { claimSummaries: [] };
            returnValue.claimSummaries = [
                {
                    publicID: 'cc:9803321',
                    claimNumber: '930129-GI',
                    lossDate: '2019-04-24T04:00:00Z',
                    lossType: 'AUTO',
                    description: 'CV lost control and spun into IV lane colliding with IV.',
                    claimState: 'open',
                    policyNumber: 'ACP BAPC3037545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:112897934',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:260065841',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5120',
                        emailAddress1: 'cgraham@carterbloodcare.org'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2019-04-29T14:55:27Z',
                    lossLocation: {
                        publicID: 'cc:260069124',
                        displayName: 'unknown, Bedford, TX 76021',
                        addressLine1: 'unknown',
                        city: 'Bedford',
                        state: 'TX',
                        postalCode: '76021',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:9448761',
                    claimNumber: '614572-GI',
                    lossDate: '2019-02-15T22:00:00Z',
                    lossType: 'AUTO',
                    description: 'IV RE CV.',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:107838415',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:244931849',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        }
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2019-02-18T15:50:05Z',
                    lossLocation: {
                        publicID: 'cc:244936962',
                        displayName: 'I-35, I-30, Fort Worth, TX 76102',
                        addressLine1: 'I-35',
                        addressLine2: 'I-30',
                        city: 'Fort Worth',
                        state: 'TX',
                        postalCode: '76102',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:9430226',
                    claimNumber: '598132-GI',
                    lossDate: '2019-02-11T17:30:00Z',
                    lossType: 'AUTO',
                    description: 'IV rear ended by OV',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:107604321',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:244327551',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        }
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2019-02-13T22:21:04Z',
                    lossLocation: {
                        publicID: 'cc:244331873',
                        displayName: '300 S Beckham Ave., 600 E Mulberry St., Tyler, TX 75702-7504',
                        addressLine1: '300 S Beckham Ave.',
                        addressLine2: '600 E Mulberry St.',
                        city: 'Tyler',
                        state: 'TX',
                        postalCode: '75702-7504',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:9421858',
                    claimNumber: '590014-GI',
                    lossDate: '2018-12-27T05:00:00Z',
                    lossType: 'AUTO',
                    description: 'CV ran red light and struck IV',
                    claimState: 'open',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:107451022',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:243967210',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5120'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2019-02-12T16:25:11Z',
                    lossLocation: {
                        publicID: 'cc:243968092',
                        displayName: 'Bedford, TX 76021',
                        city: 'Bedford',
                        state: 'TX',
                        postalCode: '76021',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:9124567',
                    claimNumber: '319388-GI',
                    lossDate: '2018-12-10T20:15:00Z',
                    lossType: 'AUTO',
                    description: 'IV hit parked and unoccupied CV.',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:103112064',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:232059828',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        }
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-12-12T00:44:56Z',
                    lossLocation: {
                        publicID: 'cc:232059853',
                        displayName: '2120 W Jefferson Blvd, Dallas, TX 75208-5517',
                        addressLine1: '2120 W Jefferson Blvd',
                        city: 'Dallas',
                        state: 'TX',
                        postalCode: '75208-5517',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:9193959',
                    claimNumber: '382951-GI',
                    lossDate: '2018-12-07T21:00:00Z',
                    lossType: 'AUTO',
                    description: 'V1 rear ended V2 on the street.',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:104120294',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:235325845',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '864-242-5365'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-12-27T15:03:46Z',
                    lossLocation: {
                        publicID: 'cc:235329934',
                        displayName: 'Forney Rd, Dallas, TX 75201',
                        addressLine1: 'Forney Rd',
                        city: 'Dallas',
                        state: 'TX',
                        postalCode: '75201',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:9201381',
                    claimNumber: '390205-GI',
                    lossDate: '2018-11-30T21:00:00Z',
                    lossType: 'AUTO',
                    description: 'V1 rear ended V2 on the street.',
                    claimState: 'open',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:104250585',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:235669644',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '864-242-5365'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-12-28T19:33:12Z',
                    lossLocation: {
                        publicID: 'cc:235670817',
                        displayName: 'Forney Rd, Dallas, TX 75201',
                        addressLine1: 'Forney Rd',
                        city: 'Dallas',
                        state: 'TX',
                        postalCode: '75201',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:8873041',
                    claimNumber: '092628-GI',
                    lossDate: '2018-10-22T04:00:00Z',
                    lossType: 'AUTO',
                    description: 'IV turned left onto Hwy 37 from Sonic and was struck by CV on the rear d/s.',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:99587876',
                        displayName: 'Carter Bloodcare',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:221058819',
                            displayName: '2205 Highway 121, Bedford, TX 76021-5950',
                            addressLine1: '2205 Highway 121',
                            city: 'Bedford',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5632',
                        emailAddress1: 'mtenuta@carterbloodcare.org'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-10-24T20:15:29Z',
                    lossLocation: {
                        publicID: 'cc:221853038',
                        displayName: '3046 Highway 322 South, Building 1, Longview, TX 75603-7006',
                        addressLine1: '3046 Highway 322 South, Building 1',
                        city: 'Longview',
                        state: 'TX',
                        postalCode: '75603-7006',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:8734359',
                    claimNumber: '967010-GH',
                    lossDate: '2018-09-29T02:00:00Z',
                    lossType: 'AUTO',
                    description: 'V1 rearended V2, V2 was pushed into a guardrail.',
                    claimState: 'open',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:97632663',
                        displayName: 'Carter Bloodcare',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:215923003',
                            displayName: '2205 Highway 121, Bedford, TX 76021-5950',
                            addressLine1: '2205 Highway 121',
                            city: 'Bedford',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5625',
                        emailAddress1: 'bcook@carterbloodcare.org'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-10-01T18:36:06Z',
                    lossLocation: {
                        publicID: 'cc:215928443',
                        displayName: 'I 35, Dallas, TX 75201',
                        addressLine1: 'I 35',
                        city: 'Dallas',
                        state: 'TX',
                        postalCode: '75201',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:8665922',
                    claimNumber: '905009-GH',
                    lossDate: '2018-09-19T04:00:00Z',
                    lossType: 'AUTO',
                    description: 'IV1 rear ended IV2',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:96794133',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:213865259',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5120'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-09-20T14:09:57Z',
                    lossLocation: {
                        publicID: 'cc:213868587',
                        displayName: 'Bedford, TX 76021',
                        city: 'Bedford',
                        state: 'TX',
                        postalCode: '76021',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:8526485',
                    claimNumber: '777448-GH',
                    lossDate: '2018-08-25T04:00:00Z',
                    lossType: 'AUTO',
                    description: 'IV/generator caught fire',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:94992114',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:209362477',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5120'
                    },
                    lossCause: 'fire',
                    incidentOnly: false,
                    reportedDate: '2018-08-27T18:36:29Z',
                    lossLocation: {
                        publicID: 'cc:209362477',
                        displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                        addressLine1: '2205 HIGHWAY 121',
                        city: 'BEDFORD',
                        state: 'TX',
                        postalCode: '76021-5950',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:8412347',
                    claimNumber: '673994-GH',
                    lossDate: '2018-08-04T14:30:00Z',
                    lossType: 'AUTO',
                    description: 'V1 made a sharp turn while parking and struck V2',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3027545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:93466194',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:205571753',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5000'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-08-07T19:34:31Z',
                    lossLocation: {
                        publicID: 'cc:205574719',
                        displayName: 'Unknown, Bedford, TX 76021',
                        addressLine1: 'Unknown',
                        city: 'Bedford',
                        state: 'TX',
                        postalCode: '76021',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:7596275',
                    claimNumber: '931975-GG',
                    lossDate: '2018-03-19T03:30:00Z',
                    lossType: 'AUTO',
                    description: 'V1 went straight at a light in a turning lane only clipping V2',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3017545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:82094753',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:176450043',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5632'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-03-19T21:57:14Z',
                    lossLocation: {
                        publicID: 'cc:176450098',
                        displayName: '2000 Block West Loop 340, Bagby St, Waco, TX 76701',
                        addressLine1: '2000 Block West Loop 340',
                        addressLine2: 'Bagby St',
                        city: 'Waco',
                        state: 'TX',
                        postalCode: '76701',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:7403545',
                    claimNumber: '757656-GG',
                    lossDate: '2018-01-26T05:00:00Z',
                    lossType: 'AUTO',
                    description: 'CVD backing from parking spot impacted IV traveling down aisle.',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3017545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:79344500',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:170047729',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5120',
                        emailAddress1: 'MTENUTA@carterbloodcare.org'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2018-02-14T21:22:19Z',
                    lossLocation: {
                        publicID: 'cc:170054929',
                        displayName: '2400 E Seminary Dr, Fort Worth, TX 76119-5502',
                        addressLine1: '2400 E Seminary Dr',
                        city: 'Fort Worth',
                        state: 'TX',
                        postalCode: '76119-5502',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:5739716',
                    claimNumber: '236029-GF',
                    lossDate: '2017-05-03T14:28:00Z',
                    lossType: 'AUTO',
                    description: 'IV struck parked/unoccupied CV',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3017545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:58191125',
                        displayName: 'CARTER BLOODCARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:121979667',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5140'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2017-05-19T14:45:08Z',
                    lossLocation: {
                        publicID: 'cc:121983245',
                        displayName: '2510 E Lakeshore Dr, Waco, TX 76705-1788',
                        addressLine1: '2510 E Lakeshore Dr',
                        city: 'Waco',
                        state: 'TX',
                        postalCode: '76705-1788',
                        country: 'US'
                    },
                    premisisID: []
                },
                {
                    publicID: 'cc:3401961',
                    claimNumber: '102456-GD',
                    lossDate: '2016-05-11T04:00:00Z',
                    lossType: 'AUTO',
                    description: 'IV backed into OV.  IV did not see OV and backed up to get off the crosswalk at a light.',
                    claimState: 'closed',
                    policyNumber: 'ACP BAPD3007545866',
                    accountNumber: 'ACP     3007545866',
                    insuredContact: {
                        publicID: 'cc:32509274',
                        displayName: 'CARTER BLOOD CARE',
                        subtype: 'Company',
                        primaryAddress: {
                            publicID: 'cc:65377060',
                            displayName: '2205 HIGHWAY 121, BEDFORD, TX 76021-5950',
                            addressLine1: '2205 HIGHWAY 121',
                            city: 'BEDFORD',
                            state: 'TX',
                            postalCode: '76021-5950',
                            country: 'US'
                        },
                        workNumber: '817-412-5120',
                        emailAddress1: 'MTenuta@carterbloodcare.org'
                    },
                    lossCause: 'vehcollision',
                    incidentOnly: false,
                    reportedDate: '2016-05-12T16:32:49Z',
                    lossLocation: {
                        publicID: 'cc:65378786',
                        displayName: 'Alma and 15th Street, Plano, TX 75075',
                        addressLine1: 'Alma and 15th Street',
                        city: 'Plano',
                        state: 'TX',
                        postalCode: '75075',
                        country: 'US'
                    },
                    premisisID: []
                }
            ];

            return returnValue;
        }
        return response;
    }
}
